import { saunaLocations, saunaLocationServices, saunaLocationPath } from "../data/sauna-locations";
import type { SaunaSlug } from "../data/sauna-services";

const EMBER = "hsl(18 82% 52%)";

type Props = { locationKey?: string; serviceSlug?: SaunaSlug; basePath?: string };

const join = (base: string, href: string) => `${base.replace(/\/+$/, "")}${href}`;

function Arrow() {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const placeOf = (key?: string) => saunaLocations.find((l) => l.key === key);

export function SaunaLocalServices({ locationKey, serviceSlug = "install", basePath = "/" }: Props) {
  const loc = placeOf(locationKey);
  if (!loc) return null;
  const place = loc.name === loc.city ? loc.city : `${loc.name}, ${loc.city}`;
  return (
    <section aria-labelledby="sauna-local-services" className="py-12 lg:py-16 border-t border-border">
      <div className="container mx-auto px-4 max-w-7xl">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] mb-2" style={{ color: EMBER }}>Services in this area</p>
        <h2 id="sauna-local-services" className="font-display font-bold text-2xl md:text-3xl mb-6">Sauna services in {place}</h2>
        <ul className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {saunaLocationServices.map((s) => {
            const active = s.slug === serviceSlug;
            return (
              <li key={s.slug}>
                <a
                  href={join(basePath, saunaLocationPath(s.slug, loc.key))}
                  aria-current={active ? "page" : undefined}
                  className={`group flex flex-col h-full rounded-2xl overflow-hidden bg-card border transition-colors ${active ? "border-[hsl(18_82%_52%)] ring-1 ring-[hsl(18_82%_52%)]" : "border-border hover:border-[hsl(18_82%_52%)]"}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={join(basePath, `/images/${s.image}`)} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                    {active && <span className="absolute top-2 left-2 rounded-full px-2.5 py-1 text-[11px] font-bold text-white" style={{ background: EMBER }}>Current service</span>}
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="font-display font-bold text-base sm:text-lg leading-tight">{s.title} <span className="sr-only">in {place}</span></h3>
                    <p className="hidden sm:block text-muted-foreground text-sm leading-relaxed mt-1 flex-1">{s.description}</p>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: EMBER }}>{active ? "You are here" : "View service"} <Arrow /></span>
                  </div>
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

export function SaunaLocationLinks({ locationKey, serviceSlug = "install", basePath = "/" }: Props) {
  const current = placeOf(locationKey);
  const service = saunaLocationServices.find((s) => s.slug === serviceSlug);
  const cities: { city: string; region: string; items: typeof saunaLocations }[] = [];
  for (const l of saunaLocations) {
    let g = cities.find((c) => c.city === l.city);
    if (!g) cities.push((g = { city: l.city, region: l.region, items: [] }));
    g.items.push(l);
  }
  const openCity = current?.city ?? cities[0]?.city;
  const label = service ? service.title.toLowerCase() : "sauna";
  return (
    <section aria-labelledby="sauna-locations" className="py-12 lg:py-16 bg-secondary/50 border-t border-border">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-2 mb-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] mb-2" style={{ color: EMBER }}>Service areas</p>
            <h2 id="sauna-locations" className="font-display font-bold text-2xl md:text-3xl">Sauna {label} by location</h2>
          </div>
          <p className="text-sm text-muted-foreground max-w-md">UK-wide enquiries. Choose a city or area; we confirm access and scheduling for your postcode.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 items-start">
          {cities.map((c) => {
            const [cityLoc, ...areas] = c.items;
            return (
              <details key={c.city} open={c.city === openCity} className="group rounded-xl bg-card border border-border open:border-[hsl(18_82%_52%/0.5)]">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-3 rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-[hsl(18_82%_52%)]">
                  <span>
                    <span className="block font-display font-bold">{c.city}</span>
                    <span className="block text-xs text-muted-foreground">{c.region} · {areas.length} areas</span>
                  </span>
                  <span aria-hidden className="text-xl leading-none transition-transform group-open:rotate-45" style={{ color: EMBER }}>+</span>
                </summary>
                <div className="px-4 pb-4">
                  <a
                    href={join(basePath, saunaLocationPath(serviceSlug, cityLoc.key))}
                    aria-current={cityLoc.key === locationKey ? "page" : undefined}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold mb-3 hover:underline underline-offset-4"
                    style={{ color: EMBER }}
                  >
                    All of {c.city} <Arrow />
                  </a>
                  {areas.length > 0 && (
                    <ul className="flex flex-wrap gap-1.5">
                      {areas.map((a) => {
                        const active = a.key === locationKey;
                        return (
                          <li key={a.key}>
                            <a
                              href={join(basePath, saunaLocationPath(serviceSlug, a.key))}
                              aria-current={active ? "page" : undefined}
                              className={`inline-block rounded-full px-3 py-1 text-xs border transition-colors ${active ? "text-white border-transparent" : "border-border text-foreground hover:border-[hsl(18_82%_52%)]"}`}
                              style={active ? { background: EMBER } : undefined}
                            >
                              {a.name}
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </details>
            );
          })}
        </div>
      </div>
    </section>
  );
}
